/**
 * Sparks (Faíscas) service — isolated from Marketplace credits.
 */
import { supabase } from '@/integrations/supabase/client';
import type {
  SparksWallet,
  SparksSettings,
  SparksLedgerEntry,
  SparksToolPricing,
  SparksTransaction,
} from '@/types/sparks';

// ── Wallet ──────────────────────────────────────────

export async function fetchSparksWallet(userId: string): Promise<SparksWallet> {
  const { data, error } = await supabase.rpc('get_or_create_sparks_wallet', {
    p_user_id: userId,
  } as never);
  if (error) throw error;
  return data as unknown as SparksWallet;
}

export async function grantStudentBonus(userId: string): Promise<SparksWallet> {
  const { data, error } = await supabase.rpc('grant_student_signup_bonus', {
    p_user_id: userId,
  } as never);
  if (error) throw error;
  return data as unknown as SparksWallet;
}


// ── Settings ────────────────────────────────────────

export async function fetchSparksSettings(): Promise<SparksSettings> {
  const { data, error } = await supabase
    .from('sparks_settings' as never)
    .select('*')
    .eq('id', 1)
    .single();
  if (error) throw error;
  return data as unknown as SparksSettings;
}

export async function updateSparksSettings(
  updates: Partial<Omit<SparksSettings, 'id' | 'updated_at'>>
): Promise<void> {
  const { error } = await supabase
    .from('sparks_settings' as never)
    .update(updates as never)
    .eq('id', 1);
  if (error) throw error;
}

// ── Ledger ──────────────────────────────────────────

export async function fetchSparksLedger(
  userId: string,
  limit = 50
): Promise<SparksLedgerEntry[]> {
  const { data, error } = await supabase
    .from('sparks_ledger' as never)
    .select('*')
    .eq('user_id', userId)
    .order('created_at', { ascending: false })
    .limit(limit);
  if (error) throw error;
  return (data ?? []) as unknown as SparksLedgerEntry[];
}

/** Admin: fetch all ledger entries (optionally filtered) */
export async function fetchAllLedger(
  limit = 100,
  filters?: { reason?: string; fromDate?: string; toDate?: string }
): Promise<SparksLedgerEntry[]> {
  let query = supabase
    .from('sparks_ledger' as never)
    .select('*')
    .order('created_at', { ascending: false })
    .limit(limit);

  if (filters?.reason) {
    query = query.eq('reason', filters.reason);
  }
  if (filters?.fromDate) {
    query = query.gte('created_at', filters.fromDate);
  }
  if (filters?.toDate) {
    query = query.lte('created_at', filters.toDate);
  }

  const { data, error } = await query;
  if (error) throw error;
  return (data ?? []) as unknown as SparksLedgerEntry[];
}

// ── Tool Pricing ────────────────────────────────────

export async function fetchSparksToolPricing(): Promise<SparksToolPricing[]> {
  const { data, error } = await supabase
    .from('sparks_tool_pricing' as never)
    .select('*')
    .order('tool_key');
  if (error) throw error;
  return (data ?? []) as unknown as SparksToolPricing[];
}

/** Fetch global average sparks cost for a tool via SECURITY DEFINER RPC */
export async function fetchToolAverageCost(toolKey: string): Promise<number> {
  const { data, error } = await supabase.rpc('get_tool_average_cost', {
    p_tool_key: toolKey,
  } as never);
  if (error || !data) return 0;
  return data as number;
}


// ── Tool Cost Update ────────────────────────────────

export async function updateToolApiCost(toolKey: string, apiCostUsd: number): Promise<void> {
  const { error } = await supabase
    .from('sparks_tool_pricing' as never)
    .update({ api_cost_usd: apiCostUsd } as never)
    .eq('tool_key', toolKey);
  if (error) throw error;
}

// ── Admin Adjust ────────────────────────────────────

export async function adminAdjustSparks(
  userId: string,
  delta: number,
  reason: string
): Promise<SparksWallet> {
  const { data, error } = await supabase.rpc('admin_adjust_sparks', {
    p_user_id: userId,
    p_delta: delta,
    p_reason: reason,
  } as never);
  if (error) throw error;
  return data as unknown as SparksWallet;
}

// ── Admin Resets ────────────────────────────────────

/** Reset tool charge metrics via SECURITY DEFINER RPC */
export async function resetToolChargeMetrics(): Promise<number> {
  const { data, error } = await supabase.rpc('admin_reset_tool_charge_metrics' as never);
  if (error) throw error;
  return (data as number) ?? 0;
}

/** Reset all user sparks via SECURITY DEFINER RPC */
export async function resetAllUserSparks(): Promise<void> {
  const { error } = await supabase.rpc('admin_reset_all_user_sparks' as never);
  if (error) throw error;
}

// ── Transactions ────────────────────────────────────

export async function fetchSparksTransactions(
  userId: string,
  limit = 20
): Promise<SparksTransaction[]> {
  const { data, error } = await supabase
    .from('sparks_transactions' as never)
    .select('*')
    .eq('user_id', userId)
    .order('created_at', { ascending: false })
    .limit(limit);
  if (error) throw error;
  return (data ?? []) as unknown as SparksTransaction[];
}

// ── Checkout (Edge Function) ────────────────────────

export async function createSparksCheckout(packageSlot: 1 | 2 | 3): Promise<string> {
  const { data, error } = await supabase.functions.invoke('sparks-checkout', {
    body: { package_slot: packageSlot },
  });
  if (error) throw error;
  if (data?.error) throw new Error(data.error);
  return data.url as string;
}

// ── Verify Payment (Direct Stripe check) ────────────

export async function verifySparksPayment(): Promise<{ credited: boolean; total_credited: number }> {
  const { data, error } = await supabase.functions.invoke('sparks-verify-session', {
    body: {},
  });
  if (error) throw error;
  if (data?.error) throw new Error(data.error);
  return { credited: data.credited ?? false, total_credited: data.total_credited ?? 0 };
}
